import axios from "axios";
import axiosRetry from "axios-retry";
import React, { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import Select, { components } from "react-select";
import { API, api_url, generateAuthHeader } from "../../util/util";
import referenceImage from "../../assets/img/reference.png";
import { Image } from "react-bootstrap";
import InfiniteScroll from "react-infinite-scroll-component"; // Import InfiniteScroll

axiosRetry(axios, {
    retries: 3,
    retryDelay: axiosRetry.exponentialDelay,
});

function NewsTable({ title }) {
    const urlTitleMap = {
        "Google News": `${api_url}/${API.ENDPOINTS.googleNews}`,
        "Tech Crunch": `${api_url}/${API.ENDPOINTS.techCrunch}`,
        "Hacker News": `${api_url}/${API.ENDPOINTS.hackerNews}`,
        "Venture Beat": `${api_url}/${API.ENDPOINTS.ventureBeat}`,
        "News API": `${api_url}/${API.ENDPOINTS.newsApi}`,
        "News": `${api_url}/${API.ENDPOINTS.news}`,
    };

    const [loading, setLoading] = useState(true);
    const [news, setNews] = useState([]);
    const [allNews, setAllNews] = useState([]); // Store all news data
    const [selectedSentiments, setSelectedSentiments] = useState([]);
    const [page, setPage] = useState(1); // Track the current page
    const [loadingMore, setLoadingMore] = useState(false); // Track if more data is loading

    const handleStreamedResponse = async (url, setFunction) => {
        try {
            setFunction([]); // Clear existing data before fetching new
            const response = await fetch(url, {
                headers: {
                    Authorization: generateAuthHeader(),
                },
            });

            if (!response.body) {
                console.error("Failed to get a readable stream from the response");
                return;
            }

            const reader = response.body
                .pipeThrough(new TextDecoderStream())
                .getReader();

            let partialData = "";
            return reader.read().then(function pump({ done, value }) {
                if (done) {
                    return;
                }
                partialData += value;
                const lines = partialData.split("\n");
                for (let i = 0; i < lines.length - 1; i++) {
                    const json = JSON.parse(lines[i]);
                    setFunction((prevItems) => {
                        const ids = new Set(prevItems.map(item => item.link));
                        if (!ids.has(json.link)) {
                            return [...prevItems, json];
                        }
                        return prevItems;
                    });
                }
                partialData = lines[lines.length - 1];
                return reader.read().then(pump);
            });
        } catch (error) {
            console.error("Error fetching stream:", error);
        }
    };

    const handleFetchNews = async () => {
        const urlParams = new URLSearchParams(window.location.search);
        const query = urlParams.get("query");
        const url = urlTitleMap[title] + `?permalink=${query}`;

        await handleStreamedResponse(url, setNews);
        setLoading(false); // Set loading to false after all news is fetched
    };

    useEffect(() => {
        handleFetchNews();
    }, [title]);

    const loadMoreData = () => {
        if (loadingMore) return;  // Prevent triggering multiple requests
        setLoadingMore(true); // Set loading state to true
        setPage((prevPage) => prevPage + 1); // Increment page number
    };

    const columns = [
        {
            name: "Title",
            selector: row => <a className="news-links" href={(row.link.startsWith('http://') || row.link.startsWith('https://') ? '' : 'https://') + row.link} target="_blank" rel="noreferrer">{row.title} <Image src={referenceImage} /></a>,
            sortable: true,
            wrap: true,
            width: "40%",
        },
        {
            name: "Publisher",
            selector: row => row.publisher,
            sortable: true,
            width: "15%",
        },
        {
            name: "Sentiment",
            selector: row => (
                <span style={{
                    backgroundColor: row.sentiment_label === "positive" ? "rgba(47, 180, 35, 1)" :
                        row.sentiment_label === "negative" ? "rgba(235, 13, 12, 1)" :
                            "rgba(254, 213, 1, 1)",
                    color: "rgba(255, 255, 255, 1)",
                    padding: "4px 8px",
                    borderRadius: "5px",
                    display: "inline-block",
                    textAlign: "center",
                    minWidth: "80px"
                }}>
                    {row.sentiment_label.charAt(0).toUpperCase() + row.sentiment_label.slice(1)}
                </span>
            ),
            sortable: true,
            width: "20%",
        },
        {
            name: "Date",
            selector: row => new Date(row.parsed_date).toLocaleDateString("en-US", {
                day: "2-digit",
                month: "long",
                year: "numeric",
            }),
            sortable: true,
            width: "15%",
        },
    ];

    const handleSentimentChange = (selectedOptions) => {
        setSelectedSentiments(selectedOptions);
    };

    const sentimentOptions = [
        { value: "positive", label: "Positive" },
        { value: "negative", label: "Negative" },
        { value: "neutral", label: "Neutral" },
    ];

    const customOption = (props) => {
        const { data, isSelected } = props;
        return (
            <components.Option {...props}>
                <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => null}
                    style={{ marginRight: 8 }}
                />
                <label>{data.label}</label>
            </components.Option>
        );
    };

    const customStyles = {
        control: (provided) => ({
            ...provided,
            borderRadius: "8px",
            padding: "4px",
            borderColor: "#ccc",
            boxShadow: "none",
            "&:hover": { borderColor: "#888" },
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor:
                state.data.value === "positive" ? "#2FB423" :
                    state.data.value === "negative" ? "#EB0D0C" :
                        state.data.value === "neutral" ? "#FED501" :
                            state.isFocused ? "#e8e8e8" : "#fff",
            color: "#ffff",
            padding: "5px 6px",
            borderRadius: "5px",
            margin: "6px",
            width: "100px",
            display: "flex",
            alignItems: "center",
        }),
        multiValue: (provided) => ({
            ...provided,
            backgroundColor: "#d3e3fc",
            color: "#1a3d7a",
            borderRadius: "4px",
            padding: "3px 8px",
        }),
        multiValueLabel: (provided) => ({
            ...provided,
            color: "#1a3d7a",
        }),
        multiValueRemove: (provided) => ({
            ...provided,
            color: "#1a3d7a",
            ":hover": {
                backgroundColor: "#b0d0ff",
                color: "#1a3d7a",
            },
        }),
    };

    const [paginatedNews, setPaginatedNews] = useState([]);
    useEffect(() => {
        const filteredNews = news.filter((item) => {
            const activeSentiments = selectedSentiments.map(sentiment => sentiment.value);
            return activeSentiments.length === 0 || activeSentiments.includes(item.sentiment_label);
        });
        if (paginatedNews.length < filteredNews.length) {
            setLoadingMore(true);
            const timeout = setTimeout(() => {
                // Paginate the filtered data to show 5 items per page
                const newsForCurrentPage = filteredNews.slice(0, page * 5); // Ensure only 5 items per page
                setPaginatedNews(newsForCurrentPage);
                setLoading(false); // Turn off loading after timeout
                setLoadingMore(false); // Set loadingMore to false after data is loaded
            }, 2000); // Set delay in milliseconds (2 seconds in this case)

            return () => clearTimeout(timeout);
        } else {
            setLoadingMore(false); // No more data to load
        }
    }, [news, page, selectedSentiments]);  // Add 'page' as a dependency so pagination updates when the page changes
    // Add 'page' as a dependency so pagination updates when the page changes

    return (
        <div className="">
            <div className="tab-1 mb-3">
                <h2>{title}</h2>
            </div>
            {loading ? (
                <div className="text-center">
                    <div className="spinner-border me-2" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p>Data is coming soon</p>
                </div>
            ) : news && news.length > 0 ? (
                <div className="table-section">
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h6>Recent Articles</h6>
                        </div>
                        <div className="filter-section d-flex align-items-center">
                            <label className="mx-1">
                                Sentiment: <span className="sentiment-count mx-2">{selectedSentiments.length}</span>
                            </label>
                            <Select
                                className="me-2"
                                options={sentimentOptions}
                                isMulti
                                placeholder="selected"
                                onChange={handleSentimentChange}
                                components={{ Option: customOption }}
                                styles={customStyles}
                            />
                        </div>
                    </div>
                    <InfiniteScroll
                        dataLength={paginatedNews.length}
                        next={loadMoreData}
                        hasMore={news.length > paginatedNews.length}
                    >
                        <div className="scrollable-container">
                            <DataTable
                                columns={columns}
                                data={loading ? [] : paginatedNews} // If loading, pass an empty array to avoid rendering table data
                                highlightOnHover
                                pointerOnHover
                            />
                        </div>
                    </InfiniteScroll>
                    {loadingMore && (
                        <div className="text-center mt-3">
                            <div className="spinner-border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <p>No data available</p>
            )}
        </div>
    );
}

export default NewsTable;
